import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Label,
  PaginationComponent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@folio/components';
import {
  ClaimStatusBadge,
  claimInsuredName,
  claimTypeLabel,
} from '@folio/components/module-elements/ClaimStatusBadge';
import { ErrorRow } from '@folio/components/module-elements/ErrorRow';
import { LoadingRow } from '@folio/components/module-elements/LoadingRow';
import { formatDate } from '@folio/lib/date-formatting';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  type PublicClaimTypeDto,
  claimManagementControllerListQueryOptions,
  configControllerGetPublicProductConfigQueryOptions,
} from 'korint-sdk';
import { useDebouncedValue, useKorintProductConfig } from 'korint-sdk/react';
import { uniqBy } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

const PAGE_SIZE = 10;

const SEARCH_DEBOUNCE_MS = 400;

const EMPTY_VALUE = '—';

// Radix Select forbids an empty item value.
const ANY_VALUE = '__any__';

/** The badge merges a main status and its sub-state, so the filter offers both. */
const STATE_FILTERS = [
  { value: 'DRAFT', param: 'status' },
  { value: 'INSTRUCTION', param: 'status' },
  { value: 'PENDING_EXTERNAL_DATA', param: 'subStatus' },
  { value: 'AWAITING_CLAIMANT_SUBMISSION', param: 'subStatus' },
  { value: 'AWAITING_DOCUMENTS', param: 'subStatus' },
  { value: 'UNDER_REVIEW', param: 'subStatus' },
  { value: 'SETTLEMENT', param: 'status' },
  { value: 'CLOSED', param: 'status' },
  { value: 'DISCARDED', param: 'status' },
] as const;

const FILTER_PARAMS = ['search', 'state', 'claimType', 'occurredFrom', 'occurredTo'] as const;

type FilterParam = (typeof FILTER_PARAMS)[number];

type FilterUpdate = Partial<Record<FilterParam, string | undefined>>;

const toDate = (iso: string | undefined, locale: string) =>
  iso ? formatDate(new Date(iso), locale) : EMPTY_VALUE;

function useClaimTypeOptions(): PublicClaimTypeDto[] {
  const { productsConfig } = useKorintProductConfig();

  return useQueries({
    queries: Object.keys(productsConfig).map(productId =>
      configControllerGetPublicProductConfigQueryOptions(productId),
    ),
    combine: results =>
      uniqBy(
        results.flatMap(({ data }) => data?.claimTypes ?? []),
        ({ id }) => id,
      ),
  });
}

function ClaimsFilters() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const claimTypes = useClaimTypeOptions();

  const setFilters = (update: FilterUpdate) =>
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      for (const [key, value] of Object.entries(update)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      next.delete('page');
      return next;
    });

  const urlSearch = searchParams.get('search') ?? '';
  const [searchInput, setSearchInput] = useState(urlSearch);
  const debouncedSearch = useDebouncedValue(searchInput.trim(), SEARCH_DEBOUNCE_MS);
  useEffect(() => {
    if (debouncedSearch !== urlSearch) setFilters({ search: debouncedSearch || undefined });
  }, [debouncedSearch]);

  const occurredFrom = searchParams.get('occurredFrom') ?? '';
  const occurredTo = searchParams.get('occurredTo') ?? '';
  const hasFilters = FILTER_PARAMS.some(key => searchParams.has(key));

  const resetFilters = () => {
    setSearchInput('');
    setFilters(Object.fromEntries(FILTER_PARAMS.map(key => [key, undefined])));
  };

  return (
    <div className="flex flex-wrap items-end gap-3 pb-4">
      <div className="grid w-72 gap-1.5">
        <Label htmlFor="claims-search">{t('claimsList.filters.search')}</Label>
        <Input
          id="claims-search"
          type="search"
          placeholder={t('claimsList.filters.searchPlaceholder')}
          value={searchInput}
          onChange={event => setSearchInput(event.target.value)}
        />
      </div>
      <div className="grid w-56 gap-1.5">
        <Label htmlFor="claims-state">{t('claimsList.filters.status')}</Label>
        <Select
          value={searchParams.get('state') ?? ANY_VALUE}
          onValueChange={value => setFilters({ state: value === ANY_VALUE ? undefined : value })}
        >
          <SelectTrigger id="claims-state">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY_VALUE}>{t('claimsList.filters.anyStatus')}</SelectItem>
            {STATE_FILTERS.map(({ value }) => (
              <SelectItem key={value} value={value}>
                {t(`claimsList.status.${value}`, value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {claimTypes.length > 0 && (
        <div className="grid w-56 gap-1.5">
          <Label htmlFor="claims-type">{t('claimsList.filters.type')}</Label>
          <Select
            value={searchParams.get('claimType') ?? ANY_VALUE}
            onValueChange={value =>
              setFilters({ claimType: value === ANY_VALUE ? undefined : value })
            }
          >
            <SelectTrigger id="claims-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_VALUE}>{t('claimsList.filters.anyType')}</SelectItem>
              {claimTypes.map(({ id, label }) => (
                <SelectItem key={id} value={id}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <fieldset className="grid gap-1.5">
        <legend className="mb-1.5 text-sm font-medium leading-none">
          {t('claimsList.filters.occurredAt')}
        </legend>
        <div className="flex gap-2">
          <Input
            type="date"
            className="w-40"
            aria-label={t('claimsList.filters.occurredFrom')}
            max={occurredTo || undefined}
            value={occurredFrom}
            onChange={event => setFilters({ occurredFrom: event.target.value || undefined })}
          />
          <Input
            type="date"
            className="w-40"
            aria-label={t('claimsList.filters.occurredTo')}
            min={occurredFrom || undefined}
            value={occurredTo}
            onChange={event => setFilters({ occurredTo: event.target.value || undefined })}
          />
        </div>
      </fieldset>
      {hasFilters && (
        <Button variant="ghost" onClick={resetFilters}>
          {t('claimsList.filters.reset')}
        </Button>
      )}
    </div>
  );
}

export function ClaimsListModule() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();

  const page = Number.parseInt(searchParams.get('page') ?? '', 10);
  const currentPage = Number.isNaN(page) || page < 1 ? 1 : page;
  const stateFilter = STATE_FILTERS.find(({ value }) => value === searchParams.get('state'));

  const {
    data: claims,
    isLoading,
    isError,
  } = useQuery({
    ...claimManagementControllerListQueryOptions({
      limit: PAGE_SIZE,
      offset: (currentPage - 1) * PAGE_SIZE,
      search: searchParams.get('search') ?? undefined,
      status: stateFilter?.param === 'status' ? stateFilter.value : undefined,
      subStatus: stateFilter?.param === 'subStatus' ? stateFilter.value : undefined,
      claimType: searchParams.get('claimType') ?? undefined,
      occurredFrom: searchParams.get('occurredFrom') ?? undefined,
      occurredTo: searchParams.get('occurredTo') ?? undefined,
    }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('claimsList.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ClaimsFilters />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('claimsList.columns.number')}</TableHead>
              <TableHead>{t('claimsList.columns.policyNumber')}</TableHead>
              <TableHead>{t('claimsList.columns.declaredAt')}</TableHead>
              <TableHead>{t('claimsList.columns.occurredAt')}</TableHead>
              <TableHead>{t('claimsList.columns.type')}</TableHead>
              <TableHead>{t('claimsList.columns.client')}</TableHead>
              <TableHead>{t('claimsList.columns.status')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: PAGE_SIZE }).map((_, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length skeleton placeholder
                <LoadingRow key={index} />
              ))
            ) : isError ? (
              <ErrorRow>{t('claimsList.error')}</ErrorRow>
            ) : !claims?.data.length ? (
              <ErrorRow>{t('claimsList.empty')}</ErrorRow>
            ) : (
              claims.data.map(claim => (
                <TableRow key={claim.id}>
                  <TableCell>
                    <Link to={`/claims/${claim.id}`} className="font-medium hover:underline">
                      {claim.id}
                    </Link>
                  </TableCell>
                  <TableCell>{claim.policyNumber ?? EMPTY_VALUE}</TableCell>
                  <TableCell>{toDate(claim.submittedAt, i18n.language)}</TableCell>
                  <TableCell>{toDate(claim.incident.occurredAt, i18n.language)}</TableCell>
                  <TableCell>{claimTypeLabel(claim)}</TableCell>
                  <TableCell>{claimInsuredName(claim) || EMPTY_VALUE}</TableCell>
                  <TableCell>
                    <ClaimStatusBadge claim={claim} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
      <CardFooter>
        <PaginationComponent
          totalData={claims?.total ?? 0}
          PAGE_SIZE={PAGE_SIZE}
          isLoading={isLoading}
        />
      </CardFooter>
    </Card>
  );
}
